import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, TrendingUp, AlertTriangle, Mail, Users, Target, Shield, Eye, Command, Calendar, Zap } from 'lucide-react';
import { DemoContactForm } from '@/components/sections/DemoContactForm';

const warRoomFeatures = [
  {
    icon: BarChart3,
    title: 'Pipeline Movement Dashboard',
    description: 'Real-time view of every deal moving through your pipeline. See what advanced, what stalled, and what slipped. Updated continuously from CRM data, meeting outcomes, and engagement signals.',
    highlights: ['Stage-by-stage velocity analytics', 'Deals created vs closed trends', 'Weighted pipeline vs target tracking'],
  },
  {
    icon: TrendingUp,
    title: 'Evidence-Based Forecasting',
    description: 'Forecasts built on verified evidence, not rep optimism. Every deal is scored against engagement signals, stakeholder coverage, and CRM completeness. Backtest calibration ensures accuracy improves over time.',
    highlights: ['Commit, Best Case, and Upside buckets', 'Backtest calibration with drift detection', 'Forecast delta tracking week-over-week'],
  },
  {
    icon: AlertTriangle,
    title: 'Risk Alerts and Going-Dark Detection',
    description: 'Know instantly when a deal is at risk. The system monitors buyer engagement velocity, response times, and multi-threading coverage to surface going-dark alerts before it is too late.',
    highlights: ['Buyer engagement scoring per deal', 'Going-dark alerts via Slack and email', 'Days-since-last-contact tracking'],
  },
  {
    icon: Users,
    title: 'Rep Performance and Coaching',
    description: 'See which reps are hitting targets and which need support. Activity metrics, conversion rates, and coaching insights from call recordings give you the data to lead effectively.',
    highlights: ['Activity tracking per rep', 'Win/loss analysis with reason codes', 'Talk-time ratios and objection patterns'],
  },
  {
    icon: Mail,
    title: 'Monday Morning Digest',
    description: 'Every Monday, your inbox contains a board-ready executive brief. Pipeline movement, forecast changes, risk alerts, and action items. No dashboard login required.',
    highlights: ['Automated weekly delivery', 'Board-ready formatting', 'Key deal highlights and risks'],
  },
  {
    icon: Target,
    title: 'Quota and Target Management',
    description: 'Set quarterly and annual targets. Track attainment in real-time against pipeline coverage. Identify gaps early enough to take action.',
    highlights: ['Quota attainment tracking', 'Pipeline coverage ratios', 'Gap analysis with recommendations'],
  },
];

const croProblems = [
  { problem: 'You spend Sunday night building forecast spreadsheets', solution: 'Automated evidence-based forecasts delivered to your inbox' },
  { problem: 'Deals slip without warning at end of quarter', solution: 'Going-dark alerts surface risks weeks before close date' },
  { problem: 'Rep pipeline reviews take 2+ hours and reveal nothing', solution: 'Every deal pre-scored with engagement data before your 1:1' },
  { problem: 'Board asks for pipeline details you cannot answer', solution: 'Board-ready narratives generated from verified data' },
  { problem: 'No visibility into whether reps are actually executing', solution: 'Activity and outcome tracking with coaching signals' },
];

export default function CommandCenter() {
  return (
    <Layout>
      <SEOHead
        title="Revenue Command Center | The CRO War Room | NoQ5.ai"
        description="The CRO War Room: pipeline movement, evidence-based forecasting, risk alerts, rep performance, and Monday Morning Digests. Built for revenue leaders."
        canonicalUrl="https://noq5.ai/product/command-center"
        keywords="CRO dashboard, revenue command center, pipeline visibility, evidence-based forecasting, sales forecast accuracy, CRO war room, revenue operations dashboard, deal risk alerts"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is the NoQ5 Command Center?", "acceptedAnswer": { "@type": "Answer", "text": "The Command Center is a real-time executive dashboard built specifically for CROs and VP Sales. It provides pipeline movement tracking, evidence-based forecasting, risk alerts, rep performance scorecards, and automated Monday Morning Digests." }},
            { "@type": "Question", "name": "How does evidence-based forecasting work?", "acceptedAnswer": { "@type": "Answer", "text": "Instead of relying on rep-submitted forecasts, NoQ5 scores every deal against verified evidence: email engagement velocity, meeting cadence, stakeholder coverage, and CRM completeness. Backtest calibration ensures accuracy improves over time." }},
            { "@type": "Question", "name": "What are going-dark alerts?", "acceptedAnswer": { "@type": "Answer", "text": "Going-dark alerts notify you when buyer engagement drops on a deal. NoQ5 monitors response times, email velocity, and multi-threading coverage to surface at-risk deals before it is too late." }},
            { "@type": "Question", "name": "Can I get pipeline updates via Slack?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. The Monday Morning CRO Digest is delivered via Slack or email. You also get real-time risk alerts and deal movement notifications directly in your Slack workspace." }},
          ]
        }]}
      />
      <Breadcrumbs items={[
        { label: "Product", href: "/product/platform" },
        { label: "Command Center", href: "/product/command-center" },
      ]} />

      {/* Hero */}
      <section className="py-20 md:py-28 bg-background relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/3 blur-[150px]" />
        </div>
        <div className="container relative px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-8">
                <Command className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground/80">Built for CROs and VP Sales</span>
              </div>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.08] mb-6"
            >
              <span className="text-foreground">The CRO </span>
              <span className="text-gradient-teal">War Room</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              Pipeline movement, forecast accuracy, risk signals, and rep performance. Everything a revenue leader needs to make decisions, not chase data.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-sm shadow-button hover:shadow-none transition-all group" asChild>
                <a href="#book-demo">See It in Action <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /></a>
              </Button>
              <Button size="lg" variant="outline" className="font-bold px-8 py-6 text-lg rounded-sm border-2 border-primary/25 hover:bg-primary/5" asChild>
                <a href="/product/platform">See Full Platform</a>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CRO Pain Points */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">Sound Familiar?</h2>
            <p className="text-lg text-muted-foreground">Every CRO faces these challenges. NoQ5 solves them automatically.</p>
          </div>
          <div className="space-y-4">
            {croProblems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-sm p-5 grid md:grid-cols-2 gap-4 items-center"
              >
                <div className="flex items-start gap-3">
                  <span className="text-destructive font-bold text-lg mt-0.5">&#10005;</span>
                  <p className="text-sm text-muted-foreground">{item.problem}</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-primary font-bold text-lg mt-0.5">&#10003;</span>
                  <p className="text-sm font-medium text-foreground">{item.solution}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Deep Dive */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">Inside the Command Center</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Six integrated modules that give CROs complete pipeline visibility without chasing reps for updates.</p>
          </div>
          <div className="max-w-5xl mx-auto space-y-8">
            {warRoomFeatures.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-sm p-6 md:p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-sm bg-primary/10">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                </div>
                <p className="text-muted-foreground mb-5 leading-relaxed">{feature.description}</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {feature.highlights.map((h) => (
                    <div key={h} className="flex items-start gap-2 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <span className="text-foreground">{h}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why CROs Choose NoQ5 */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4 text-foreground">Built for Revenue Leaders, Not Sales Reps</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Sales reps use NoQ5 because it eliminates their admin. But the real value flows to CROs: clean data, accurate forecasts, and complete visibility.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Eye, title: 'Full Visibility', desc: 'Every deal scored on evidence. Every risk surfaced automatically. No more surprises.' },
              { icon: Calendar, title: 'Time Back', desc: 'Replace 2-hour pipeline reviews with 15-minute data-driven conversations.' },
              { icon: Shield, title: 'Board Confidence', desc: 'Forecasts backed by engagement data and backtest calibration, not gut feel.' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-sm p-6 text-center"
              >
                <item.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-background">
        <div className="container px-4 md:px-6 max-w-3xl mx-auto text-center">
          <div className="bg-primary rounded-sm p-8 md:p-12">
            <Zap className="w-10 h-10 text-primary-foreground mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">See Your Pipeline Like Never Before</h3>
            <p className="text-primary-foreground/80 mb-6">Book a 20-minute demo and see the Command Center with your own pipeline data.</p>
            <Button size="lg" className="bg-background text-foreground hover:bg-background/90 font-bold px-8 py-6 text-lg" asChild>
              <a href="#book-demo">Book a Demo <ArrowRight className="ml-2 w-5 h-5" /></a>
            </Button>
          </div>
        </div>
      </section>

      <DemoContactForm />
    </Layout>
  );
}
